import Image from 'next/image';
import { useMemo } from 'react';
import { NETWORKS } from 'src/common/types/network';

import useGetChainInfo from '@/hooks/useGetChainInfo';
import { getAddressScannerUrl, maskAddressValue } from '@/utils/format';

import { EventTriggerType, Trigger } from '../../domain';
import { TriggerExternalLinkDetail } from './components/TriggerExternalLinkDetail';
import { TriggerType } from './components/TriggerType';

interface Props {
  trigger: Trigger;
  triggerType: EventTriggerType;
}

export const PendleTriggerDetails = ({ trigger, triggerType }: Props) => {
  const { getChainIconUrl } = useGetChainInfo();

  const ytAddress = useMemo(() => {
    if (trigger.context?.ytAddress) {
      return trigger.context.ytAddress;
    }

    if (trigger.signature) {
      const match = trigger.signature.match(/@([^@]+)$/);
      return match ? match[1] : '';
    }

    return '';
  }, [trigger.context?.ytAddress, trigger.signature]);

  const networkInfo = useMemo(() => {
    if (trigger.context?.network) {
      return NETWORKS.find((network) => network.name.toLowerCase() === trigger.context?.network?.toLowerCase());
    }

    if (trigger.description) {
      const match = trigger.description.match(/on\s+(\w+)$/i);
      if (match) {
        const networkName = match[1].toLowerCase();
        return NETWORKS.find((network) => network.name.toLowerCase() === networkName);
      }
    }

    return null;
  }, [trigger.context?.network, trigger.description]);

  const scannerUrl = useMemo(() => {
    if (!ytAddress || !networkInfo) return '';
    return getAddressScannerUrl('chainId', networkInfo.chainId, ytAddress) || '';
  }, [ytAddress, networkInfo]);

  const chainIconUrl = useMemo(() => {
    if (!networkInfo) return '';
    return getChainIconUrl(networkInfo.chainId) || '';
  }, [networkInfo, getChainIconUrl]);

  return (
    <div className="tw:flex tw:flex-row tw:gap-6">
      <TriggerType eventType={triggerType} />

      <TriggerExternalLinkDetail
        label="YT Address"
        value={
          <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center">
            {chainIconUrl && <Image src={chainIconUrl} width={12} height={12} alt={networkInfo?.name || 'chain'} />}
            <span>{maskAddressValue(ytAddress) ?? ''}</span>
          </div>
        }
        href={scannerUrl}
      />
    </div>
  );
};
